import React, { useMemo, useEffect } from 'react';
import DataTable from './DataTable';
import WidgetBase from './WidgetBase';
import { useWidgetData } from '../../hooks/useWidgetData';
import { exportToCSV } from '../../utils/exportToCSV';
import { getFilterKeyFromColumnHeader, processDashboardUpdate } from '../../utils/processDashboardUpdate';

/**
 * Table Widget Component
 * Modernized replacement for List.js
 * Pure table display with sorting, filtering, pagination
 * Most flexible - handles all table types
 */
function TableWidget({ data, handlechangea, onExportReady, onLegendUpdate, onDataReady }) {
    // Fetch data using custom hook
    const { data: apiData, loading, error } = useWidgetData(
        data.url,
        data.RequestJson,
        data.RequestJson1
    );

    // Transform API data to table format
    // API returns listDataSet: array of {datasets: [{label, data}]} rows
    const { tableData, columns } = useMemo(() => {
        if (!apiData || apiData.length === 0) {
            return { tableData: [], columns: [] };
        }

        try {
            const listDataSet = apiData[0].listDataSet;

            if (!listDataSet || listDataSet.length === 0) {
                return { tableData: [], columns: [] };
            }

            // First row's datasets define the column headers (header text is in .data field)
            const cols = (listDataSet[0].datasets || []).map((ds, idx) => ({
                key: `col${idx}`,
                label: ds.data || `Column ${idx + 1}`,
                sortable: true,
                format: (val) => val ?? '-'
            }));

            // Data rows start at index 1 (index 0 is the header row)
            const rows = listDataSet.slice(1).map(item => {
                const row = {};
                (item.datasets || []).forEach((ds, idx) => {
                    row[`col${idx}`] = ds.data;
                });
                return row;
            });

            return { tableData: rows, columns: cols };
        } catch (err) {
            console.error('Error transforming table data:', err);
            return { tableData: [], columns: [] };
        }
    }, [apiData]);

    // Per-cell click handler — resolves the filter key from each cell's column label.
    // DataType on the widget config acts as fallback when the column label isn't a direct match.
    // DataType: "NA" (or absent) means the widget is not filterable at all.
    const isFilterable = !!(handlechangea && data.DataType !== 'NA');

    const handleCellClick = (row, columnKey, columnLabel) => {
        if (!handlechangea) return;
        // Resolve from column header first; fall back to data.DataType for widgets where
        // the column header text doesn't match the filter key directly (e.g. custom labels).
        const filterKey = getFilterKeyFromColumnHeader(columnLabel)
            || (data.DataType && data.DataType !== 'NA' ? data.DataType : null);
        if (!filterKey) return;
        const cellValue = row[columnKey];
        if (!cellValue || String(cellValue) === 'Total') return;
        // For city-type values strip the suffix after a comma (e.g. "DALLAS, TX" → "DALLAS")
        const resolvedValue = filterKey === 'PhCity'
            ? String(cellValue).split(',')[0].trim()
            : String(cellValue);
        processDashboardUpdate(filterKey, resolvedValue, () => handlechangea && handlechangea());
    };

    // Notify parent whether this widget has data (used to disable toggle / export buttons).
    // Use derived tableData — the raw apiData can be non-empty yet still contain no rows.
    useEffect(() => {
        if (loading) return;
        if (onDataReady) onDataReady(tableData.length > 0);
    }, [loading, tableData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Export handler
    const handleExport = () => {
        if (tableData.length > 0) {
            exportToCSV(tableData, data.title || 'table-data', columns);
        }
    };

    // Register export handler with parent (for header-level export button)
    useEffect(() => {
        if (onExportReady) onExportReady(handleExport);
    }, [tableData]);

    // Report legend value to parent for CardTitle count display
    // Matches old List.js: apidata[0].legend[0].legendValue
    useEffect(() => {
        if (!apiData || apiData.length === 0 || !onLegendUpdate) return;
        try {
            const legendVal = apiData[0].legend?.[0]?.legendValue ?? '';
            if (legendVal) onLegendUpdate(data.id, String(legendVal));
        } catch (e) { /* ignore */ }
    }, [apiData]);

    return (
        <WidgetBase
            loading={loading}
            error={error}
            chartComponent={null} // Table-only widget — single-mode path in WidgetBase
            tableComponent={
                tableData.length > 0 ? (
                    <DataTable
                        columns={columns}
                        data={tableData}
                        onCellClick={isFilterable ? handleCellClick : null}
                        pagination={{ enabled: true, itemsPerPage: 6 }}
                        sortable={true}
                        searchable={false}
                    />
                ) : (
                    <div className="flex items-center justify-center py-12">
                        <p className="text-primary/70">No Data Available</p>
                    </div>
                )
            }
            onExport={null}
            showToggle={false}
            showExport={false}
        />
    );
}

export default TableWidget;
